import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Col } from "antd";
import { useHistory } from "react-router-dom";
import { ServerSideDataGrid } from "common/components/serverSideDataGrid/serverSideDataGrid";
import { selectUser, selectUserId } from "redux/slices/userSlice";
import { JobStatusSelector } from "features/v2/job/status/jobStatusSelector";
import { usePageFilter } from "hooks/usePageFilters";
import { DataTableLayout } from "common/components/dataTableLayout/dataTableLayout";
import Job from "redux/models/job";
import K from "utilities/constants";
import AgGridUtility from "utilities/agGridUtility";
import { getColumns } from "./columns";
import { CreateJobButton } from "./createJobButton/createJobButton";
import { ExportButton } from "./exportButton/exportButton";

const jobToTableRow = (job) => ({
  id: job.id,
  title: job.title,
  extAtsId: job.extAtsId,
  extSecondaryAtsId: job.extSecondaryAtsId,
  recruitingStartDate: job.recruitingStartDate,
  currentOpenings: job.currentOpenings,
  requisitionAging: job.requisitionAging,
  minPay: job.minPay,
  maxPay: job.maxPay,
  isCancel: job.isCancel,
  client: job.client,
  jobLocation: job.jobLocation,
  jobStatus: job.jobStatus,
  jobDepartment: job.jobDepartment,
  jobGroup: job.jobGroup,
  jobTier: job.jobTier,
  jobTierTitle: job.jobTierTitle,
  jobRecruiters: job.jobRecruiters,
  originalVacancy: job.originalVacancy,
  jobHiringManagers: job.jobHiringManagers,
  notes: job.notes,
});

export const ClientJobsContainer = ({ clientId }) => {
  const history = useHistory();
  const userId = useSelector(selectUserId);
  const userSlice = useSelector(selectUser);

  const pagePrefix = `client_${clientId}_jobs`;

  const [search, setSearch] = usePageFilter(`${pagePrefix}_search`);
  const [status, setStatus] = usePageFilter(`${pagePrefix}_status`);

  const statusId = status?.value;

  const gridRef = useRef(null);

  const [exportData, setExportData] = useState({
    jobIds: [],
    isDisabled: true,
  });

  const getServerSideData = useCallback(
    (options) => {
      const params = {
        ...options,
        status: statusId,
        search: search ?? undefined,
      };

      return Job.v2.getAllByClient(clientId, params);
    },
    [clientId, search, statusId],
  );

  const onStatusChange = (statusId, status) => setStatus(status);

  const onSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    setExportData((prev) => ({
      ...prev,
      isDisabled:
        selectedRows.length === 0 &&
        !event.api.getServerSideSelectionState().selectAll,
      jobIds: selectedRows.map(({ id }) => id),
    }));
  };

  const onJobCreated = (job) => {
    AgGridUtility.onServerSideRowCreated(
      gridRef.current?.api,
      jobToTableRow(job),
    );
  };

  const onJobUpdated = (job) => {
    AgGridUtility.onServerSideRowsUpdated(gridRef.current?.api, [
      jobToTableRow(job),
    ]);
  };

  const onJobDeleted = (job) => {
    AgGridUtility.onServerSideRowDeleted(gridRef.current?.api, job.id);
  };

  const columns = useMemo(
    () => getColumns(clientId, userSlice.roles, onJobUpdated, onJobDeleted),
    [clientId, userSlice.roles],
  );

  const onCellClicked = (params) => {
    if (params.colDef.field === "actions") {
      return;
    }

    history.push(`/clients/${clientId}/job/${params.data.id}`);
  };

  const getExportParams = () => {
    const { columnApi } = gridRef.current;
    const sortColumn = columnApi.getColumnState().find((c) => c.sort);

    return {
      search: search ?? undefined,
      sortBy: sortColumn?.colId,
      sortOrder: sortColumn?.sort,
    };
  };

  return (
    <DataTableLayout
      searchDefaultValue={search}
      onSearchChange={setSearch}
      renderFilters={() => (
        <Col style={{ width: 200 }}>
          <JobStatusSelector defaultValue={status} onChange={onStatusChange} />
        </Col>
      )}
      actions={[
        {
          renderer: () => (
            <Col>
              <ExportButton
                clientId={clientId}
                jobIds={exportData.jobIds}
                jobStatus={statusId}
                disabled={exportData.isDisabled}
                getParams={getExportParams}
                onSuccess={() => gridRef.current?.api?.deselectAll()}
              />
            </Col>
          ),
        },
        {
          permissions: [K.Permissions.CreateJob],
          renderer: () => (
            <Col>
              <CreateJobButton clientId={clientId} onSuccess={onJobCreated} />
            </Col>
          ),
        },
      ]}
    >
      <ServerSideDataGrid
        ref={gridRef}
        userId={userId}
        configKey={K.AgGridTable.Keys.ClientJobsVisibleColumn}
        rowSelection="multiple"
        onSelectionChanged={onSelectionChanged}
        getData={getServerSideData}
        defaultColumnConfig={columns}
        rowStyle={{ cursor: "pointer" }}
        onCellClicked={onCellClicked}
        suppressRowClickSelection
      />
    </DataTableLayout>
  );
};
